import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import WasherNotes from './WasherNotes';

const WrappedHeader = (props) => (
  <div
    style={{
      whiteSpace: 'normal',
      lineHeight: 'normal',
      textAlign: 'center',
      padding: '4px 0',
    }}
  >
    {props.colDef.headerName}
  </div>
);

const columns7089 = [
  { field: 'id', headerName: 'ID', hide: true },
  {
    field: 'name',
    headerName: 'Size',
    width: 72,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'd1',
    headerName: 'd₁\nhole Ø\n(mm)',
    type: 'number',
    width: 88,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'd2',
    headerName: 'd₂\nouter Ø\n(mm)',
    type: 'number',
    width: 88,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'h',
    headerName: 'h\n(mm)',
    type: 'number',
    width: 64,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
];

const columns436 = [
  { field: 'id', headerName: 'ID', hide: true },
  {
    field: 'name',
    headerName: 'Size',
    width: 72,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'hole',
    headerName: 'Hole Ø\n(mm)',
    type: 'number',
    width: 80,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'side',
    headerName: 'Side\n(mm)',
    type: 'number',
    width: 80,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'h',
    headerName: 'h\n(mm)',
    type: 'number',
    width: 64,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
  {
    field: 'notes',
    headerName: 'Notes',
    flex: 1,
    minWidth: 200,
    renderHeader: (p) => <WrappedHeader {...p} />,
  },
];

const WashersTable = () => {
  const [subTab, setSubTab] = useState(0);
  const [rows7089, setRows7089] = useState([]);
  const [rows436, setRows436] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [r1, r2] = await Promise.all([
          fetch('/WASHERS_ISO7089.json'),
          fetch('/WASHERS_DIN436.json'),
        ]);
        if (!r1.ok || !r2.ok) throw new Error('Failed to load washer data');
        const [d1, d2] = await Promise.all([r1.json(), r2.json()]);
        if (!cancelled) {
          setRows7089(Array.isArray(d1) ? d1 : []);
          setRows436(Array.isArray(d2) ? d2 : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Could not load washer JSON files');
          setRows7089([]);
          setRows436([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeRows = subTab === 0 ? rows7089 : rows436;
  const filteredRows = useMemo(() => {
    if (!filterText.trim()) return activeRows;
    const q = filterText.toLowerCase();
    return activeRows.filter(
      (r) =>
        String(r.name || '').toLowerCase().includes(q) ||
        String(r.d || '').includes(q)
    );
  }, [activeRows, filterText]);

  const columns = subTab === 0 ? columns7089 : columns436;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
        ISO 7089 — plain washers (normal series). DIN 436 — square washers (typical timber / wood–steel applications).
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
          <Tab label="ISO 7089" />
          <Tab label="DIN 436" />
        </Tabs>
      </Box>

      <Box mb={2}>
        <TextField
          label="Filter by size"
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: 520 }}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            getRowId={(r) => r.id}
            checkboxSelection={false}
            disableRowSelectionOnClick
            density="compact"
            initialState={{
              columns: { columnVisibilityModel: { id: false } },
            }}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'action.hover',
                lineHeight: 'normal',
              },
            }}
          />
        </Paper>
      )}

      <WasherNotes />
    </Box>
  );
};

export default WashersTable;
